<template>
    <v-lazy
        :min-height="80"
        transition="fade-transition"
        class="search-result-list-item"
        :class="cssClasses"
        @mouseenter="$emit('hover', true)"
        @mouseleave="$emit('hover', false)"
    >
        <v-card elevation="0" class="search-result-list-item__card">
            <v-card-title v-ripple class="search-result-list-item__header cursor-pointer text-truncate" @click="$emit('click')">
                <slot v-bind="{ active, expanded }" />
                <slot v-if="active" name="append" v-bind="{ active, expanded }" />
            </v-card-title>
            <v-card-text class="search-result-list-item__details">
                <v-expand-transition>
                    <div v-if="expanded">
                        <slot name="details" v-bind="{ active, expanded }" />
                    </div>
                </v-expand-transition>
            </v-card-text>
            <div class="search-result-list-item__focus-indicator"></div>
        </v-card>
    </v-lazy>
</template>

<script setup lang="ts">
import { computed, nextTick, watch } from 'vue';
import { type VCard } from 'vuetify/components';

interface Props extends /* @vue-ignore */ NonNullable<InstanceType<typeof VCard>['$props']> {
    /**
     * Whether the item is disabled
     */
    disabled?: boolean;

    /**
     * Whether to expand the item when it is activated
     */
    expandOnActivate?: boolean;
}

interface Emits {
    (e: 'click'): void;
    (e: 'hover', value: boolean): void;
}

const props = defineProps<Props>();
defineEmits<Emits>();
const active = defineModel<boolean>('active');
const expanded = defineModel<boolean>('expanded');
/**
 * Whether the item is focused
 */
const focused = defineModel<boolean>('focused');

const cssClasses = computed(() => [
    {
        'search-result-list-item--active': active.value,
        'search-result-list-item--focused': focused.value,
        'search-result-list-item--expanded': expanded.value,
        'search-result-list-item--disabled': props.disabled
    }
]);

watch(
    active,
    value => {
        if (value) {
            if (props.expandOnActivate) {
                nextTick(() => (expanded.value = true));
            }
        }
    },
    { immediate: true }
);

defineExpose({ active, expanded });
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.search-result-list-item {
    --list-item-height: 80px;
    --list-item-bg-color: var(--v-color-white, #fff);
    --list-item-focus-indicator-border-width: 1px;
    --list-item-focus-indicator-border-color: var(--v-color-grey-lighten-2);
    --list-item-focus-indicator-border-radius: #{$spacer * 3};
    --list-item-card-bg-color: transparent;

    margin: $spacer * 2 0;
    background-color: var(--list-item-bg-color);
    scroll-margin-top: $spacer * 4;

    &--disabled {
        filter: opacity(0.4);
        pointer-events: none;
    }

    &__focus-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        border-width: var(--list-item-focus-indicator-border-width, 1px);
        border-style: solid;
        border-color: var(--list-item-focus-indicator-border-color);
        border-radius: var(--list-item-focus-indicator-border-radius);

        .search-result-list-item--active &,
        .search-result-list-item--focused & {
            --list-item-bg-color: #f5fcf8 !important;
            --list-item-focus-indicator-border-width: 2px;
            --list-item-focus-indicator-border-color: rgb(var(--v-theme-primary));
        }
    }

    &__card {
        background-color: var(--list-item-card-bg-color);
        border-radius: var(--list-item-focus-indicator-border-radius);

        .search-result-list-item--active &,
        .search-result-list-item--focused & {
            --list-item-card-bg-color: var(--v-color-green-50);
        }

        .search-result-list-item:not(
                .search-result-list-item--expanded,
                .search-result-list-item--active,
                .search-result-list-item--focused
            ):hover
            & {
            --list-item-card-bg-color: var(--v-color-neutral-50);
        }
    }

    &__header {
        min-height: var(--list-item-height);
        transition: padding-top 0.2s ease-in-out;
        padding: 0;
    }

    &__details {
        padding: 0 $spacer * 3;
    }
}
</style>
